import type { z } from 'zod';

import { createFullNameSchema } from '@/app/lib/forms/schemas/base-fields.schema';
import {
  REGEX_CHARACTERS_SPACE,
  REGEX_FULLNAME_INVALID,
  REGEX_ONLY_NUMBER,
} from '@/app/utils/regex';
import logger from '@/lib/logger';

export const sanitizeFullnameInput = (value: string) => {
  if (value === '') return '';

  if (!REGEX_CHARACTERS_SPACE.test(value)) {
    return;
  }

  const noLeadingSpace = value.replace(/^\s+/, '');
  const normalized = noLeadingSpace.replace(/\s+/g, ' ');

  if (normalized.trim().length === 0) {
    return '';
  }

  const spaceCount = (normalized.match(/ /g) || []).length;

  if (spaceCount > 1) {
    return;
  }

  return normalized;
};

export const transformToOnlyNumber = (value: string) => {
  if (!REGEX_ONLY_NUMBER.test(value)) {
    return value.replace(/\D/g, '');
  }
  return value;
};

export const validateThaiIdCard = (id: string) => {
  if (id.length !== 13 || !/^\d{13}$/.test(id)) return false;

  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(id.charAt(i)) * (13 - i);
  }
  const checkSum = (11 - (sum % 11)) % 10;
  return checkSum === parseInt(id.charAt(12));
};

type FormState = { isSubmitted: boolean };

export const createFullnameFieldValidator = (
  formState?: FormState | (() => FormState)
) => {
  const getFormState =
    typeof formState === 'function' ? formState : () => formState;

  return {
    transform: (value: string) => {
      let filtered = value.replace(REGEX_FULLNAME_INVALID, '');
      const sanitized = sanitizeFullnameInput(filtered);
      if (!sanitized) {
        filtered = filtered.replace(/\s+$/, '');
        const parts = filtered.trim().split(/\s+/);
        filtered =
          parts.length > 1
            ? parts[0] + ' ' + parts.slice(1).join('')
            : parts[0] || '';
        return sanitizeFullnameInput(filtered) ?? filtered;
      }
      return sanitized;
    },
    onChange: (value: string | undefined) => {
      const currentState = getFormState();

      if (
        (!value && currentState?.isSubmitted === false) ||
        (value && value.length < 2)
      ) {
        return undefined;
      }

      const validationResult = createFullNameSchema().safeParse(value);

      if (!validationResult.success) {
        return validationResult.error.issues[0]?.message;
      }

      return undefined;
    },
  };
};

export const flattenValidationErrors = (
  errors: Record<string, string[] | undefined>
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(errors).map(([k, v]) => [k, v?.[0] || ''])
  );
};

export const getValidationErrors = (
  validationResult: z.ZodSafeParseError<unknown>
): Record<string, string> => {
  const errors = validationResult.error.flatten().fieldErrors;
  const flatErrors = flattenValidationErrors(errors);
  logger.error('Validation errors:', flatErrors);
  return flatErrors;
};
